import * as SelectPrimitive from '@radix-ui/react-select';
import {CheckIcon} from 'lucide-react';
import {forwardRef, type ReactNode, type Ref, useId, useState} from 'react';
import {cn} from '../../lib/cn';
import type {SelectOption} from '../../lib/selector-option';
import {LegacySelectContent, LegacySelectTrigger} from './components';

type LegacyMultiSelectProps<
  D,
  V extends string | number = string,
  L extends string | number | ReactNode = string,
> = {
  label?: string | ReactNode;
  className?: string;
  options?: SelectOption<D, V, L>[];
  children?: ReactNode;
  value?: (string | number)[];
  onChange?: (value: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

const LegacyMultiSelectInner = <
  V extends string | number = string,
  D = unknown,
  L extends string | number | ReactNode = string,
>(
  {
    label,
    value = [],
    onChange,
    className,
    options,
    placeholder,
    disabled,
  }: LegacyMultiSelectProps<D, V, L>,
  ref: Ref<HTMLDivElement>,
) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const hasValue = value.length > 0;
  const showLabel = hasValue || open;

  const handleSelect = (selectedValue: string) => {
    const newValues = value.some(v => String(v) === selectedValue)
      ? value.filter(v => String(v) !== selectedValue)
      : [...value, selectedValue];
    onChange?.(newValues);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!disabled) {
      setOpen(newOpen);
    }
  };

  const isSelected = (optionValue: string | number) => {
    return value.some(v => String(v) === String(optionValue));
  };

  const displayText =
    value.length > 0 ? `${value.length} selected` : placeholder || 'Select options';

  return (
    <div ref={ref} className={cn('group relative w-[300px]', className)}>
      {label && showLabel && (
        <label
          htmlFor={id}
          className="absolute start-1 top-0 z-10 block -translate-y-1/2 bg-white px-2 text-xs font-medium text-[var(--chekin-color-brand-navy)] group-has-[:disabled]:opacity-50"
        >
          {label}
        </label>
      )}
      <SelectPrimitive.Root
        open={open}
        onOpenChange={handleOpenChange}
        value=""
        disabled={disabled}
      >
        <LegacySelectTrigger id={id}>
          <span className="text-sm">{displayText}</span>
        </LegacySelectTrigger>
        <LegacySelectContent>
          {options?.map(({value: optionValue, label: optionLabel}) => {
            const stringValue = String(optionValue);
            const selected = isSelected(optionValue);

            return (
              <div
                key={stringValue}
                role="option"
                aria-selected={selected}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSelect(stringValue);
                }}
                className={cn(
                  'relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 pe-2 ps-8 text-sm outline-none hover:bg-[var(--chekin-gray-50)] hover:text-[var(--chekin-color-brand-navy)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  selected && 'bg-[var(--chekin-gray-50)]',
                )}
              >
                <span className="absolute start-2 flex size-3.5 items-center justify-center">
                  {selected && <CheckIcon size={16} strokeWidth={2} />}
                </span>
                <span>{optionLabel}</span>
              </div>
            );
          })}
        </LegacySelectContent>
      </SelectPrimitive.Root>
    </div>
  );
};

export const LegacyMultiSelect = forwardRef(LegacyMultiSelectInner) as <
  V extends string | number = string,
  D = unknown,
  L extends string | number | ReactNode = string,
>(
  props: LegacyMultiSelectProps<D, V, L> & {ref?: Ref<HTMLDivElement>},
) => ReturnType<typeof LegacyMultiSelectInner>;
export type {LegacyMultiSelectProps};
